<template>
  <header class="top-bar p-3 sticky top-0 z-10" :class="userTypeClass">
    <div class="flex align-items-center justify-content-between">
      <div class="flex align-self-start">
        <Button class="p-button-text p-button-rounded menu-button p-button-lg mr-3 lg:hidden"
          @click="emit('toggle-sidebar')" icon="pi pi-bars" />
      </div>
      <div class="flex align-self-start gap-2 pt-2 nav-buttons">
        <Button class="p-button-text btn-topbar p-button-sm" label="Learning Library" />
        <Button class="p-button-text btn-topbar p-button-sm" label="Exam Preparation" @click="isDialogVisible = true"/>
        <Button class="p-button-text btn-topbar p-button-sm" label="About Us" @click="goTo('/aboutUs')"/>
        <!-- <Button class="p-button-text btn-topbar p-button-sm" icon="pi pi-comments" /> -->
        <Button class="p-button-text btn-topbar p-button-sm" icon="pi pi-desktop" @click="dashboardFn" />
        <GoogleTranslate class="px-2 pt-2"/>

        <!-- <div class="flex justify-center md:justify-start">
          <img :src="image" alt="Profile Image" class="profile-image" />
        </div> -->
        <div class="flex align-items-center cursor-pointer" @click="toggleMenu">
          <img :src="image" alt="Profile Image" class="profile-image" />
          <i class="pi pi-code font-semibold icon-topbar" style="transform: rotate(90deg); margin-left: 4px;"></i>
        </div>
        <Menu ref="menu" :model="menuItems" :popup="true">
          <template #item="{ item, props }">
            <a v-bind="props.action" class="p-menuitem-link" style="cursor: pointer;">
              <span class="p-menuitem-text" :style="item.customStyle">{{ item.label }}</span>
            </a>
          </template>
        </Menu>
      </div>
    </div>
    <div class="flex flex-column justify-content-center align-items-center mt-6">
      <slot></slot>
    </div>

    <Teleport to="body">
      <DeleteAccountVerify v-if="currentDeleteStep === 1" :role="props.role" :is-loading="isDeleting" :error="deleteError" @next="handleVerifyNext" @cancel="cancelDelete" />
      <DeleteAccountPreCheck v-if="currentDeleteStep === 2" :role="props.role" :is-loading="isPreChecking" :has-pending="preCheckResult?.hasPending ?? false" :messages="preCheckResult?.messages ?? []" :note="preCheckResult?.note ?? null" :error="preCheckError" @next="handlePreCheckNext" @retry="fetchPreDeleteCheck" @cancel="cancelDelete" />
      <DeleteAccountConfirm v-if="currentDeleteStep === 3" :role="props.role" @next="handleConfirmNext" @cancel="cancelDelete" />
      <DeleteAccountReason v-if="currentDeleteStep === 4" :role="props.role" :is-loading="isDeleting" @next="handleReasonNext" @cancel="cancelDelete" />
      <DeleteAccountSuccess v-if="currentDeleteStep === 5" :role="props.role" @done="handleSuccessDone" />
    </Teleport>
    <ComingSoonDialog v-model="isDialogVisible" :duration="3000" />
  </header>
</template>
<script setup>
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import { computed, defineEmits, defineProps, ref} from 'vue';
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import GoogleTranslate from '@/components/GoogleTranslate.vue';
import DeleteAccountConfirm from '@/components/settings/deleteAccount/DeleteAccountConfirm.vue';
import DeleteAccountPreCheck from '@/components/settings/deleteAccount/DeleteAccountPreCheck.vue';
import DeleteAccountReason from '@/components/settings/deleteAccount/DeleteAccountReason.vue';
import DeleteAccountVerify from '@/components/settings/deleteAccount/DeleteAccountVerify.vue';
import DeleteAccountSuccess from '@/components/settings/deleteAccount/DeleteAccountSuccess.vue';
import * as authApi from '@/api/auth';
import * as studentApi from '@/api/student';
import * as tutorApi from '@/api/tutor';
import * as instituteApi from '@/api/institutes';
import ComingSoonDialog from '../ComingSoonDialog.vue';

const emit = defineEmits(['toggle-sidebar']);
const router = useRouter();
const authStore = useAuthStore();
const props = defineProps({
  role: String,
  image: String,
  user: Object
});
const isDialogVisible = ref(false);

const userTypeClass = computed(() => {
  switch (props.role) {
    case 'student':
      return 'topbar-student';
    case 'tutor':
      return 'topbar-tutor';
    case 'institute':
      return 'topbar-institution';
    default:
      return 'topbar-default';
  }
});

const dashboardFn = () => {
  switch (props.role) {
    case 'student':
      return router.push('/student_dashboard');
    case 'tutor':
      return router.push('/tutor_dashboard');
    case 'institute':
      return router.push('/ourTutors');
    default:
      return router.push('/student_dashboard');
  }
}

const logout = () => {
  authStore.logout();
};

// Menu Logic
const menu = ref();
const toggleMenu = (event) => {
    menu.value.toggle(event);
};

const currentDeleteStep = ref(0);
const deleteOptions = ref({});
const isDeleting = ref(false);
const deleteError = ref(null);

const preCheckResult = ref(null);
const preCheckError = ref(null);
const isPreChecking = ref(false);

const startDeleteAccount = () => {
    currentDeleteStep.value = 1;
};

// Step 1 — verify password
const handleVerifyNext = async (password) => {
    isDeleting.value = true;
    deleteError.value = null;
    try {
        await authApi.verifyPassword({ password });
        deleteOptions.value = { ...deleteOptions.value, password };
        currentDeleteStep.value = 2;
        fetchPreDeleteCheck();
    } catch (error) {
        deleteError.value = error.response?.data?.message || 'Incorrect password. Please try again.';
    } finally {
        isDeleting.value = false;
    }
};

// Step 2 — pre-delete check (auto-skips to Confirm if nothing pending)
const fetchPreDeleteCheck = async () => {
    isPreChecking.value = true;
    preCheckError.value = null;
    preCheckResult.value = null;
    try {
        let response;
        if (props.role === 'student') {
            response = await studentApi.preDeleteCheck();
        } else if (props.role === 'tutor') {
            response = await tutorApi.preDeleteCheck();
        } else if (props.role === 'institute') {
            response = await instituteApi.preDeleteCheck();
        }
        const result = response.data.data;
        preCheckResult.value = result;
        if (!result.hasPending) {
            currentDeleteStep.value = 3;
        }
    } catch (error) {
        preCheckError.value = error.response?.data?.message || 'Failed to check account status. Please try again.';
    } finally {
        isPreChecking.value = false;
    }
};

const handlePreCheckNext = () => {
    currentDeleteStep.value = 3;
};

// Step 3 — confirm options
const handleConfirmNext = (options) => {
    deleteOptions.value = { ...deleteOptions.value, ...options };
    currentDeleteStep.value = 4;
};

// Step 4 — reason/feedback → call delete API
const handleReasonNext = async (feedback) => {
    deleteOptions.value = { ...deleteOptions.value, feedback: feedback || null };
    isDeleting.value = true;
    deleteError.value = null;

    const payload = {
        wipeData: deleteOptions.value.wipeData ?? false,
        sendEmail: deleteOptions.value.sendEmail ?? false,
        feedback: deleteOptions.value.feedback,
    };

    try {
        if (props.role === 'student') {
            await studentApi.deleteAccount(payload);
        } else if (props.role === 'tutor') {
            await tutorApi.deleteAccount(payload);
        } else if (props.role === 'institute') {
            await instituteApi.deleteAccount({ ...payload, removeAssociatedMembers: deleteOptions.value.removeAssociatedMembers ?? false });
        }
        currentDeleteStep.value = 5;
    } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete account. Please try again.');
    } finally {
        isDeleting.value = false;
    }
};

const handleSuccessDone = () => {
    currentDeleteStep.value = 0;
    logout();
};

const cancelDelete = () => {
    currentDeleteStep.value = 0;
    deleteOptions.value = {};
    deleteError.value = null;
    preCheckResult.value = null;
    preCheckError.value = null;
    isPreChecking.value = false;
};

const menuItems = computed(() => [
    { label: 'Dashboard', command: () => dashboardFn(), customStyle: 'color: black;' },
    { label: 'Home', command: () => router.push('/'), customStyle: 'color: black;' },
    // { label: 'Cancel Subscription', customStyle: 'color: black;', visible: props.role === 'institute' },
    { label: 'Delete Account', command: startDeleteAccount, customStyle: 'color: #e24c4c; font-weight: 500;' },
    { label: 'Logout', command: logout, customStyle: 'color: black;' }
]);

const goTo = (path) => {
  router.push(path);
};
</script>
<style scoped>
.top-bar {
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--surface-border);
  height: 40vh !important;
  transition: background 0.5s ease-in-out;
}

.topbar-default {
  background: linear-gradient(360deg, #77d8d8 -35.88%, #005f5f 100%);
}

.topbar-student {
  background: linear-gradient(360deg, #ffb8c5 -35.88%, #a72e44 100%);
}

.topbar-tutor {
  background: linear-gradient(360deg, #77d8d8 -35.88%, #005f5f 100%);
}

.topbar-institution {
  background: var(--yellow-500);
}

.btn-topbar {
  color: white;
  font-size: 14px;
  font-weight: 400;
}

.btn-topbar:hover {
  background-color: transparent !important;
  color: white !important;
  transform: scale(1.05);
}

.topbar-institution .btn-topbar {
  color: #1d1d1d;
}

.topbar-institution .btn-topbar:hover {
  color: #1d1d1d !important;
}

.icon-topbar {
  color: white;
}

.topbar-institution .icon-topbar {
  color: #1d1d1d;
}

.menu-button {
  background-color: transparent;
  color: white;
  border: none;
}

.menu-button:hover {
  background-color: transparent !important;
  border: none !important;
  color: white !important;
  transform: scale(1.05);
}

.profile-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;

}

@media (max-width: 760px) {
  .nav-buttons>button:nth-child(-n + 3) {
    display: none !important;
  }

  .top-bar {
    height: auto !important;
  }
}
</style>
